import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { mapConfigAtom } from 'pages/area-geometries/show/atoms';
import Map from 'components/map/map';
import { PolygonArea } from 'components/map';
import { buildPolygonStyle } from 'lib/map';
import styles from './area-map.module.scss';
import sectionStyles from './config-sections.module.scss';
import { useEffect, useState } from 'react';
import { useScenarioForm } from './contexts';
import type { Geometry } from 'geojson';
import commonStyles from './common.module.scss';
import { atom, useAtomValue } from 'jotai';
import type { MapRef } from 'react-map-gl';
import turfBbox from '@turf/bbox';

const BASE_STYLE = buildPolygonStyle({
  color: '#FFB784',
  fillOpacity: 0.15,
  lineOpacity: 0.6,
  lineWidth: 2,
});
const SELECTED_STYLE = buildPolygonStyle({
  color: '#FFAFD2',
  fillOpacity: 0.3,
  lineOpacity: 0.6,
  lineWidth: 2,
});
const SUB_AREA_STYLE = buildPolygonStyle({
  color: '#0f62fe',
  fillOpacity: 0.2,
  lineOpacity: 0.7,
  lineWidth: 2,
});

const tcMapRefAtom = atom<MapRef | null>(null);

export const AreaMap = () => {
  const [expanded, setExpanded] = useState(true);
  const {
    selectedConfigId,
    areaConfigs,
    selectedAreaConfig,
    subAreaGeometries,
    focusedSubAreaId,
    setFocusedSubAreaId,
    isScenarioDetailsSelected,
  } = useScenarioForm();
  const mapRef = useAtomValue(tcMapRefAtom);

  const selectedAreaGeoJson = selectedAreaConfig?.areaGeometry?.geoJson as
    Geometry | null | undefined;

  // Focus the map on the selected config's basin area whenever the user
  // picks a config or the basin area geometry resolves. Keyed on
  // selectedConfigId so re-selecting a config still re-centers the camera.
  //
  // - fitBounds with `linear: true` delegates to easeTo internally, which
  //   always interpolates linearly to the target camera (flyTo, the default,
  //   can render as a near no-op when the target bbox already sits inside
  //   the current viewport).
  // - requestAnimationFrame defers the camera change one frame so it always
  //   runs after the Map component's own `jumpTo({ longitude, latitude,
  //   zoom })` effect, which fires on the same mapRef change at mount and
  //   would otherwise clobber our zoom.
  // - `essential: true` ensures the animation runs even when the user has
  //   prefers-reduced-motion enabled.
  useEffect(() => {
    if (!mapRef) return;
    if (!selectedAreaGeoJson) return;

    const [minLng, minLat, maxLng, maxLat] = turfBbox(
      selectedAreaGeoJson as any
    );

    if (
      !Number.isFinite(minLng) ||
      !Number.isFinite(minLat) ||
      !Number.isFinite(maxLng) ||
      !Number.isFinite(maxLat)
    ) {
      return;
    }

    const map = mapRef.getMap();
    const frame = requestAnimationFrame(() => {
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: 40,
          maxZoom: 15,
          duration: 800,
          linear: true,
          essential: true,
        }
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [mapRef, selectedConfigId, selectedAreaGeoJson]);

  // Focus the map on ALL selected sub-areas when the user picks from the
  // FilterableMultiSelect. `focusedSubAreaId` is set by the selection
  // handler only during interactive picks (not on initial data load),
  // so this effect won't fight the basin-area focus above on page load.
  // Each newly fetched geometry triggers this effect, progressively
  // expanding the viewport to fit all loaded sub-area geometries.
  useEffect(() => {
    if (!mapRef || !focusedSubAreaId) return;

    const geometries = Object.values(subAreaGeometries);
    if (geometries.length === 0) return;

    const collection = {
      type: 'FeatureCollection' as const,
      features: geometries.map((g) => ({
        type: 'Feature' as const,
        geometry: g,
        properties: {},
      })),
    };

    const [minLng, minLat, maxLng, maxLat] = turfBbox(collection as any);

    if (
      !Number.isFinite(minLng) ||
      !Number.isFinite(minLat) ||
      !Number.isFinite(maxLng) ||
      !Number.isFinite(maxLat)
    ) {
      return;
    }

    const map = mapRef.getMap();
    const frame = requestAnimationFrame(() => {
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: 40,
          maxZoom: 15,
          duration: 800,
          linear: true,
          essential: true,
        }
      );
      // Clear after fitBounds so the same sub-area can be re-focused if
      // deselected and re-selected. Must be inside the RAF — clearing
      // synchronously in the effect body triggers a re-render whose
      // cleanup cancels this frame before it executes.
      setFocusedSubAreaId(null);
    });

    return () => cancelAnimationFrame(frame);
  }, [mapRef, focusedSubAreaId, subAreaGeometries, setFocusedSubAreaId]);

  // Focus the map on all area config geometries when Scenario Details is
  // selected, giving the user an overview of every created area.
  useEffect(() => {
    if (!mapRef || !isScenarioDetailsSelected) return;

    const geoJsons = areaConfigs
      .filter((c) => c.areaGeometry?.geoJson)
      .map((c) => c.areaGeometry!.geoJson as Geometry);

    if (geoJsons.length === 0) return;

    const collection = {
      type: 'FeatureCollection' as const,
      features: geoJsons.map((g) => ({
        type: 'Feature' as const,
        geometry: g,
        properties: {},
      })),
    };

    const [minLng, minLat, maxLng, maxLat] = turfBbox(collection as any);

    if (
      !Number.isFinite(minLng) ||
      !Number.isFinite(minLat) ||
      !Number.isFinite(maxLng) ||
      !Number.isFinite(maxLat)
    ) {
      return;
    }

    const map = mapRef.getMap();
    const frame = requestAnimationFrame(() => {
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: 40,
          maxZoom: 15,
          duration: 800,
          linear: true,
          essential: true,
        }
      );
    });

    return () => cancelAnimationFrame(frame);
  }, [mapRef, isScenarioDetailsSelected, areaConfigs]);

  return (
    <Accordion
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      className={styles.accordion}
    >
      <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
        <h2 className={commonStyles.configFormTitle}>Map</h2>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.mapContainer}>
          <Map
            className={styles.mapFill}
            longitude={-98}
            latitude={31}
            zoom={4}
            mapConfigAtom={mapConfigAtom}
            refAtom={tcMapRefAtom}
            showControls
            showMapFilters
          >
            {areaConfigs
              .filter((c) => c.areaGeometry?.geoJson)
              .map((c) => (
                <PolygonArea
                  key={c.id}
                  id={`tc-area-${c.id}`}
                  geometry={c.areaGeometry!.geoJson as Geometry}
                  style={
                    c.id === selectedAreaConfig?.id
                      ? SELECTED_STYLE
                      : BASE_STYLE
                  }
                />
              ))}
            {Object.entries(subAreaGeometries).map(([id, geometry]) => (
              <PolygonArea
                key={`sub-area-${id}`}
                id={`tc-sub-area-${id}`}
                geometry={geometry as Geometry}
                style={SUB_AREA_STYLE}
              />
            ))}
          </Map>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
